// =============================================================================
// FILE: stores/sessionResume.store.ts
// PURPOSE: R2 Session Resume — tracks where the user left off so R2 can
//   greet them back and restore context immediately.
//   Persists last route, timestamp, last action, and pending work summary
//   across browser sessions.
// =============================================================================

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface SessionSnapshot {
  route: string;
  routeLabel: string;
  routeIcon: string;
  timestamp: string;           // ISO — when they last left this route
  lastAction?: string;         // last undo engine entry label
  lastActionCategory?: string;
  pendingCount?: number;       // e.g. open workflows, pending approvals
  context?: string;            // free-form "you were working on X"
  sessionDurationMs?: number;  // how long they were on that route
}

export interface SessionResumeState {
  snapshots: SessionSnapshot[];   // most-recent first (max 5)
  lastLoginAt: string | null;
  totalSessionCount: number;
  dismissedAt: string | null;     // when user dismissed the banner
  showBanner: boolean;

  // ── Actions ───────────────────────────────────────────────────────────────
  recordRouteVisit(snapshot: Omit<SessionSnapshot, 'timestamp'>): void;
  recordLogin(): void;
  dismissBanner(): void;
  shouldShowBanner(): boolean;
  getLastSession(): SessionSnapshot | null;
  clearHistory(): void;
}

const ROUTE_META: Record<string, { label: string; icon: string }> = {
  'dashboard':         { label: 'Command Dashboard',      icon: '◈' },
  'projects':          { label: 'Projects',               icon: '🏗️' },
  'assets':            { label: 'Asset Registry',         icon: '📦' },
  'buildings':         { label: 'Buildings',              icon: '🏢' },
  'clashes':           { label: 'Clash Detection',        icon: '⚡' },
  'documents':         { label: 'Documents',              icon: '📄' },
  'doc-control':       { label: 'Document Control',       icon: '🔐' },
  'automation-studio': { label: 'Automation Studio',      icon: '⚡' },
  'cie':               { label: 'R2 Daily Adventures',    icon: '🎨' },
  'ai-assistant':      { label: 'R2 Brain',               icon: '🤖' },
  'schedule':          { label: 'Schedule',               icon: '📅' },
  'settings':          { label: 'Settings',               icon: '⚙️' },
  'r2-brain':          { label: 'R2 Brain',               icon: '🧠' },
  'comms':             { label: 'Communications',         icon: '💬' },
  'cabling':           { label: 'Cabling',                icon: '🔌' },
  'sync':              { label: 'Sync',                   icon: '🔄' },
  'field':             { label: 'Field Operations',       icon: '🏗️' },
  'autopilot':         { label: 'Autopilot',              icon: '🚀' },
  'global-command':    { label: 'Mission Control',        icon: '🌐' },
};

export function getRouteMeta(route: string): { label: string; icon: string } {
  return ROUTE_META[route] ?? { label: route.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), icon: '→' };
}

export function formatElapsed(isoTimestamp: string): string {
  const diff = Date.now() - new Date(isoTimestamp).getTime();
  const mins = Math.floor(diff / 60_000);
  const hours = Math.floor(diff / 3_600_000);
  const days = Math.floor(diff / 86_400_000);
  if (mins < 2)   return 'just now';
  if (mins < 60)  return `${mins} minutes ago`;
  if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  if (days === 1) return 'yesterday';
  return `${days} days ago`;
}

export const useSessionResume = create<SessionResumeState>()(
  persist(
    (set, get) => ({
      snapshots: [],
      lastLoginAt: null,
      totalSessionCount: 0,
      dismissedAt: null,
      showBanner: false,

      recordRouteVisit(snapshot) {
        const meta = getRouteMeta(snapshot.route);
        const entry: SessionSnapshot = {
          ...snapshot,
          routeLabel: snapshot.routeLabel || meta.label,
          routeIcon:  snapshot.routeIcon  || meta.icon,
          timestamp:  new Date().toISOString(),
        };
        set(s => ({
          snapshots: [entry, ...s.snapshots.filter(x => x.route !== snapshot.route)].slice(0, 5),
        }));
      },

      recordLogin() {
        set(s => ({
          lastLoginAt: new Date().toISOString(),
          totalSessionCount: s.totalSessionCount + 1,
          showBanner: s.snapshots.length > 0,
          dismissedAt: null,
        }));
      },

      dismissBanner() {
        set({ showBanner: false, dismissedAt: new Date().toISOString() });
      },

      shouldShowBanner() {
        const { showBanner, dismissedAt, snapshots } = get();
        if (!showBanner || snapshots.length === 0) return false;
        if (!dismissedAt) return true;
        // Re-show if dismissed more than 4 hours ago (new session)
        return Date.now() - new Date(dismissedAt).getTime() > 4 * 3_600_000;
      },

      getLastSession() {
        const { snapshots } = get();
        // Return most recent non-dashboard snapshot, else most recent
        return snapshots.find(s => s.route !== 'dashboard') ?? snapshots[0] ?? null;
      },

      clearHistory() {
        set({ snapshots: [], showBanner: false, dismissedAt: null });
      },
    }),
    {
      name: 'r2-session-resume-v1',
    }
  )
);
